/**
 * Salsa ↔ Restaurant-App postMessage protocol.
 *
 * THIS FILE IS THE SINGLE SOURCE OF TRUTH for the message contract
 * between salsa (microproduct SPAs) and restaurant-app (host shell).
 *
 * Restaurant-app copies this file via: yarn salsa-protocol:sync
 * Do NOT edit the copy in restaurant-app directly — edit here, then sync.
 */

/** Message types flowing between salsa SPAs and the host app. */
export enum SalsaMessageType {
  /** SPA requests an auth token from the host. */
  AUTH_TOKEN_REQUEST = 'getAuthToken',
  /** Host responds with an auth token. */
  AUTH_TOKEN_RESPONSE = 'authToken',
  /** SPA reports an error to the host for Sentry capture. */
  ERROR = 'salsaError',
  /** Host advertises its navigation capabilities to the SPA on load. */
  HOST_CAPABILITIES = 'host:capabilities',
  /** SPA requests navigation to a different microproduct. */
  NAVIGATE_MICROPRODUCT = 'salsa:navigate',
  /** SPA requests navigation to a native host screen. */
  NAVIGATE_HOST = 'salsa:navigate-host',
  /** Host reports a navigation request failed. */
  NAVIGATE_ERROR = 'host:navigate-error',
  /** SPA requests the host to go back. */
  BACK = 'salsa:back',
  /** SPA reports its in-product route changed so the host can mirror it into
   *  its own shareable URL. The route is an opaque, product-encoded hash —
   *  the host stores it verbatim and never parses it. */
  ROUTE_CHANGED = 'salsa:route-changed',
  /** SPA reports its page-body scroll so native hosts can drive scroll-linked
   *  chrome (e.g. the bottom nav's hide-on-scroll). A WebView's own scroll
   *  events never fire for in-product scrolling — the SPA's PageLayout scrolls
   *  an inner container, not the document — so the SPA bridges the metrics.
   *  Only sent in native WebView hosts; web hosts render no scroll-linked
   *  chrome around the embed. */
  BODY_SCROLL = 'salsa:body-scroll',
  /** SPA requests the host to toggle its sidebar. Host decides based on its
   *  viewport whether to toggle the drawer (mobile) or the inline sidebar
   *  collapsed state (desktop). */
  SIDEBAR_TOGGLE = 'salsa:sidebar-toggle',
  /** Host reports current sidebar visibility so SPAs can conditionally render
   *  their own toggle affordance (hide when sidebar is fully visible). */
  SIDEBAR_STATE = 'host:sidebar-state',
}

/**
 * Native host routes that microproducts can navigate to.
 *
 * These are friendly aliases — the host maps them to its internal route names.
 * Microproducts should NEVER use internal route names like 'SalesPortalOrderEntry'.
 *
 * For navigating to OTHER microproducts, use navigateToProduct() instead —
 * available products are already known via /apps/manifest.json.
 */
export enum SalsaHostRoute {
  /** Navigate to Order Entry with a restaurant's cart. Params: { supplierUUID, restaurantUUID } */
  ORDER_ENTRY = 'order-entry',
}

/** Params for navigation messages. */
export interface SalsaNavigationParams {
  [key: string]: string;
}

/**
 * Host surface a microproduct is being rendered on.
 *
 * - 'sales-hub': supplier-facing Sales Portal (v2 experience). The historical
 *   default — hosts that predate this field send nothing, so products must
 *   treat an absent surface as 'sales-hub'.
 * - 'storefront': restaurant-facing Operator Portal (v1 experience). The
 *   host additionally provides `restaurantUUID` in the context.
 *
 * Which products appear on which surface is governed by the microproducts
 * GA slot config (Calyx), not by anything in this repo — this type only
 * tells a running product where it is, so it can adapt copy/behavior.
 */
export type SalsaSurface = 'sales-hub' | 'storefront';

/** All message types that flow from SPA → host. */
export type SalsaToHostMessage =
  | { type: SalsaMessageType.AUTH_TOKEN_REQUEST; requestId: string }
  | { type: SalsaMessageType.ERROR; error: { message: string }; context: unknown }
  | {
      type: SalsaMessageType.NAVIGATE_MICROPRODUCT;
      productId: string;
      params?: SalsaNavigationParams;
    }
  | { type: SalsaMessageType.NAVIGATE_HOST; route: SalsaHostRoute; params?: SalsaNavigationParams }
  | { type: SalsaMessageType.BACK }
  | {
      type: SalsaMessageType.ROUTE_CHANGED;
      /**
       * Verbatim `window.location.hash` (e.g. "#/customer/<uuid>/item/<uuid>",
       * or "#/" for the root route). Opaque to the host — carried, never parsed.
       */
      route: string;
      /**
       * `true` = in-place update (router `replace`, popstate, or mount resync).
       * `false` = a new logical step (router `push`).
       *
       * Web hosts always mirror via `history.replaceState` regardless — the
       * iframe's own `pushState` already participates in the tab's joint
       * session history, so the browser back button walks the drill stack;
       * a host `pushState` would double-count it. The flag is for native
       * WebView hosts, whose history is not joined to a browser tab and which
       * drive their own back stack from `push` vs `replace`.
       */
      replace: boolean;
    }
  | {
      type: SalsaMessageType.BODY_SCROLL;
      /** Total height of the scrollable content, in px. */
      contentHeight: number;
      /** Current vertical scroll offset from the top, in px. */
      offsetY: number;
      /** Height of the visible scroll viewport, in px. */
      viewportHeight: number;
    }
  | { type: SalsaMessageType.SIDEBAR_TOGGLE };

/** All message types that flow from host → SPA. */
export type HostToSalsaMessage =
  | {
      type: SalsaMessageType.AUTH_TOKEN_RESPONSE;
      requestId: string;
      token: string;
    }
  | { type: SalsaMessageType.HOST_CAPABILITIES; supportedRoutes: SalsaHostRoute[] }
  | { type: SalsaMessageType.NAVIGATE_ERROR; route: string; reason: string }
  | {
      type: SalsaMessageType.SIDEBAR_STATE;
      /** Host sidebar is collapsed (desktop) or closed drawer (mobile). */
      isCollapsed: boolean;
      /** Host is in drawer-layout mode (small viewport or tablet portrait). */
      isDrawerLayout: boolean;
    };
